import React, { useEffect, useMemo } from 'react';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import OptoutAllMessagesWrapper from '@atlassian/optout-all-messages';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import Nav4RecommendationsQuery, {
	type Nav4RecommendationsQuery as Nav4RecommendationsQueryType,
} from './__generated__/Nav4RecommendationsQuery.graphql';
import { Recommendations } from './recommendations.tsx';

/*
At the moment, growthRecommendations fragment in GQL returns different response each time. This results in a behaviour where recommendation component in UI sometimes appear and disappear
as user interacts with the page.

Further observations were:
1) User clicks on a page. It may causes a change in url. this results in the refetch of Nav4ProjectsContent fragment to keep projects recent section up-to-date.
As part of this refetch, growthRecommendations is also refetched as it is part of the same fragment.

2) On each refetch, backend returns different entity ids for recommendations.
3) As a result, It causes the behavior where a recommendation appears and then disappears on different user interactions.

In order to solve it, a below cache is implemented. The purpose of which is to cache the first response and then use it to render the recommendations component which will also be wrapped
in a memo to avoid further re-renders.
*/
const cache: { growthRecommendations: string[] } = { growthRecommendations: [] };

export function Nav4Recommendations(): React.JSX.Element | null {
	const [queryRef, loadQuery] =
		useQueryLoader<Nav4RecommendationsQueryType>(Nav4RecommendationsQuery);

	const cloudId = useCloudId();
	const userId = useAccountId();

	useEffect(() => {
		if (cloudId) {
			loadQuery({ cloudId });
		}
	}, [cloudId, loadQuery]);

	if (!queryRef) {
		return null;
	}

	if (!fg('optout_all_messages_grw-1467')) {
		return <Nav4RecommendationsInner queryRef={queryRef} />;
	}

	return (
		<OptoutAllMessagesWrapper
			touchpointId="grw-1467-jpd-recommendation-nav4"
			cloudId={cloudId}
			userId={userId}
		>
			<Nav4RecommendationsInner queryRef={queryRef} />
		</OptoutAllMessagesWrapper>
	);
}

export type Nav4RecommendationsProps = {
	queryRef: PreloadedQuery<Nav4RecommendationsQueryType>;
};

const Nav4RecommendationsInner = ({ queryRef }: Nav4RecommendationsProps) => {
	const data = usePreloadedQuery<Nav4RecommendationsQueryType>(
		graphql`
			query Nav4RecommendationsQuery($cloudId: ID!) {
				jira {
					canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
						@optIn(to: "JiraAction")
				}
				growthRecommendations {
					recommendations(
						context: {
							useCase: "jiraProjectMenuRecommendations"
							tenantId: $cloudId
							product: "jira"
						}
						first: 2
					) {
						... on GrowthRecRecommendations {
							data {
								... on GrowthRecJiraTemplateRecommendation {
									entityId
								}
							}
							context {
								sessionId
							}
						}
					}
				}
			}
		`,
		queryRef,
	);
	const canCreateProject = data?.jira?.canCreateProject ?? false;

	const recommendations = useMemo(() => {
		const appRecommendations =
			data?.growthRecommendations?.recommendations?.data
				?.map((item) => item.entityId)
				.filter(Boolean) ?? [];

		if (cache.growthRecommendations.length === 0 && appRecommendations.length > 0) {
			cache.growthRecommendations = appRecommendations;
		}

		/*
			if user has no permission for creating a project, then we want early return to performance benefit.
			This is because, currently there are only one recommendations 1) JPD Recommendation
			Which requires create project permission before user can be considered eligible
		*/

		return (
			Boolean(canCreateProject) &&
			cache.growthRecommendations.length > 0 && (
				<Recommendations
					appRecommendations={cache.growthRecommendations}
					sessionId={data?.growthRecommendations?.recommendations?.context?.sessionId}
				/>
			)
		);
	}, [canCreateProject, data]);

	return recommendations;
};
